<template>
    <div class="relative w-full">
        <!-- Background layer: grows from container width to the full viewport while the bar is pinned. -->
        <div
            class="absolute inset-y-0 left-1/2 -translate-x-1/2 bg-gray-50 transition-[width] duration-150"
            :class="isStuck ? 'w-screen' : 'w-full'"
            aria-hidden="true"
        />

        <!-- min-h: the tabs fill in after mount, so the bar keeps its height instead of jumping. -->
        <DragScrollTrack ref="trackComponent" class="relative min-h-14.5">
            <button
                v-for="(shortcut, index) in shortcuts"
                :key="`shortcut_${index}_${shortcut.title}`"
                :data-shortcut-index="index"
                type="button"
                class="shrink-0 cursor-pointer border-b-2 px-6 py-4 font-semibold whitespace-nowrap transition-colors hover:text-primary"
                :class="activeIndex === index ? 'border-primary text-primary' : 'border-transparent text-gray-700'"
                @click.prevent="onShortcutClick(shortcut)"
            >
                {{ shortcut.title }}
            </button>
        </DragScrollTrack>
    </div>
</template>

<script setup lang="ts">
import DragScrollTrack from '@/components/slider/DragScrollTrack.vue';
import { useEventListener, useMutationObserver } from '@vueuse/core';
import _ from 'lodash';
import { computed, onBeforeUnmount, onMounted, shallowRef, useTemplateRef, watch } from 'vue';

/**
 * The tabs are the titled sections on the page, in the order they appear on screen. A section opts in with
 * data-shortcut-title="<its title>", so the bar follows whatever the Prismic body and fallbacks render.
 * Sections side by side inside a [data-shortcut-group] (the product details group) share one tab named after it.
 */
type Section = {
    title: string;
    element: HTMLElement;
    group: HTMLElement | null;
    top: number;
    left: number;
};

/** One tab: a section, or several side by side on the same row of one group. */
type Shortcut = {
    title: string;
    elements: HTMLElement[];
    top: number;
    group: HTMLElement | null;
};

withDefaults(
    defineProps<{
        isStuck?: boolean;
    }>(),
    { isStuck: false },
);

// Rows closer than this count as the same row.
const sameRowTolerance = 2;

const sectionSelector = '[data-shortcut-title]';

const shortcuts = shallowRef<Shortcut[]>([]);

// A pinned sticky section reports where it is stuck, not where it sits; its parent keeps the real row.
const layoutRect = (element: HTMLElement): DOMRect =>
    (getComputedStyle(element).position === 'sticky' ? (element.parentElement ?? element) : element).getBoundingClientRect();

// Screen order, not source order: CSS can move a section, like the FAQs to the bottom of the details grid.
const collectSections = (): Section[] =>
    _(Array.from(document.querySelectorAll<HTMLElement>(sectionSelector)))
        .filter((element: HTMLElement) => !!element.dataset.shortcutTitle)
        .map((element: HTMLElement): Section => {
            const rect = layoutRect(element);

            return {
                element,
                title: element.dataset.shortcutTitle as string,
                group: element.closest<HTMLElement>('[data-shortcut-group]'),
                top: Math.round(rect.top),
                left: rect.left,
            };
        })
        .sortBy(['top', 'left'])
        .value();

const isSameGroupRow = (row: Shortcut | undefined, section: Section): row is Shortcut =>
    row !== undefined && section.group !== null && row.group === section.group && Math.abs(row.top - section.top) <= sameRowTolerance;

// Only sections of the same group on the same row merge. The bar shows from md, where the group is a grid; below md the group is the accordion and the bar is hidden.
const collectShortcuts = (): Shortcut[] =>
    _.reduce(
        collectSections(),
        (rows: Shortcut[], section: Section): Shortcut[] => {
            const previous = _.last(rows);

            if (isSameGroupRow(previous, section)) {
                previous.elements.push(section.element);
                previous.title = section.group?.dataset.shortcutGroup ?? previous.title;

                return rows;
            }

            rows.push({ title: section.title, elements: [section.element], top: section.top, group: section.group });

            return rows;
        },
        [],
    );

let refreshFrame: number | undefined;

// Batched per frame: sections mount, unmount and change their title as the page fills in.
const refreshShortcuts = (): void => {
    if (refreshFrame !== undefined) {
        return;
    }

    refreshFrame = requestAnimationFrame(() => {
        refreshFrame = undefined;
        shortcuts.value = collectShortcuts();
    });
};

onMounted(() => {
    shortcuts.value = collectShortcuts();
});

const touchesSection = (node: Node): boolean =>
    node instanceof Element && (node.matches(sectionSelector) || node.querySelector(sectionSelector) !== null);

// Sliders, reviews and accordions change the DOM all the time; only a titled section coming or going needs a refresh.
const onMutations = (mutations: MutationRecord[]): void => {
    const isRelevant = _.some(
        mutations,
        (mutation: MutationRecord) => mutation.type === 'attributes' || _.some([...mutation.addedNodes, ...mutation.removedNodes], touchesSection),
    );

    if (!isRelevant) {
        return;
    }

    refreshShortcuts();
};

useMutationObserver(() => (typeof document === 'undefined' ? undefined : document.body), onMutations, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-shortcut-title'],
});

// A breakpoint can change which section comes first on screen.
useEventListener('resize', refreshShortcuts);

// The tab only highlights while its section crosses the spy band below the sticky bar.
const intersectingElements = shallowRef<Set<Element>>(new Set());

const activeIndex = computed((): number | undefined => {
    const intersecting = _.filter(shortcuts.value, (shortcut: Shortcut) =>
        _.some(shortcut.elements, (element: HTMLElement) => intersectingElements.value.has(element)),
    );

    if (intersecting.length === 0) {
        return undefined;
    }

    // The lowest row in the band wins.
    const lowestShortcut = _.maxBy(intersecting, (shortcut: Shortcut) => shortcut.elements[0].getBoundingClientRect().top);

    return lowestShortcut ? _.indexOf(shortcuts.value, lowestShortcut) : undefined;
});

// The track can be narrower than the tabs, so keep the active tab in view.
const trackComponent = useTemplateRef<InstanceType<typeof DragScrollTrack>>('trackComponent');

watch(activeIndex, (index: number | undefined) => {
    if (index === undefined) {
        return;
    }

    const track = trackComponent.value?.trackRef;
    const activeButton = track?.querySelector<HTMLElement>(`[data-shortcut-index="${index}"]`);

    if (!track || !activeButton) {
        return;
    }

    const centeredScrollLeft = activeButton.offsetLeft - (track.clientWidth - activeButton.clientWidth) / 2;
    track.scrollTo({ left: centeredScrollLeft, behavior: 'smooth' });
});

let observer: IntersectionObserver | undefined;

const disconnectObserver = (): void => {
    observer?.disconnect();
    observer = undefined;
};

const observeSections = (): void => {
    disconnectObserver();
    intersectingElements.value = new Set();

    observer = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]): void => {
            const updated = new Set(intersectingElements.value);

            entries.forEach((entry: IntersectionObserverEntry): void => {
                if (entry.isIntersecting) {
                    updated.add(entry.target);

                    return;
                }

                updated.delete(entry.target);
            });

            intersectingElements.value = updated;
        },
        // Band from just under the sticky bar to 40% of the viewport decides the active section.
        { rootMargin: '-80px 0px -60% 0px' },
    );

    shortcuts.value.forEach((shortcut: Shortcut): void => shortcut.elements.forEach((element: HTMLElement): void => observer?.observe(element)));
};

watch(shortcuts, observeSections);

onBeforeUnmount(() => {
    disconnectObserver();

    if (refreshFrame !== undefined) {
        cancelAnimationFrame(refreshFrame);
    }
});

// Clears the pinned bar (~58px) plus breathing room, so the section title lands below it.
const scrollOffset = 80;

function onShortcutClick(shortcut: Shortcut): void {
    const top = shortcut.elements[0].getBoundingClientRect().top + window.pageYOffset - scrollOffset;
    window.scrollTo({ top, behavior: 'smooth' });
}
</script>
